import { Button, Label, Table, Text } from "north.js";
import React, { useMemo, useState } from "react";
import styled from "styled-components";

import {
  Divider,
  Header,
  PageWrapper,
  Spinner,
  successToast,
  errorToast,
} from "src/shared/components";
import { EVENT_ID } from "src/shared/constants/event";
import globalConstants from "src/theme/globalConstants";

import { useApproveLotteryEntryMutation } from "../graphql/approveLotteryEntry.generated";
import { useGetHardwareLotteryEntriesQuery } from "../graphql/getHardwareLotteryEntries.generated";
import { getRandomHardwareLoadingMessage } from "../inventory/shared/loadingTextUtil";
import { useGetHardwareInfo } from "../shared/useGetHardwareInfo";

type LotteryEntry = {
  id: number;
  user_id: number;
  user?: {
    id: number;
    name: string;
  };
  status: string;
  created_at: string;
  hardware_request_id?: number | null;
};

const OrganizerLottery: React.FC = () => {
  const [selectedSku, setSelectedSku] = useState<string>("");

  // Loading message for spinner
  const loadingMessage = useMemo(() => {
    return getRandomHardwareLoadingMessage();
  }, []);

  const {
    inventoryItems,
    hardwareCategoriesDataLoading,
    hardwareSkusDataLoading,
    hardwareItemsLoading,
  } = useGetHardwareInfo({ includeItems: true });

  const {
    data: lotteryEntriesData,
    loading: lotteryEntriesLoading,
    refetch,
  } = useGetHardwareLotteryEntriesQuery({
    variables: {
      sku_code: selectedSku,
      event_id: EVENT_ID,
    },
    skip: !selectedSku,
  });

  const [approveLotteryEntry] = useApproveLotteryEntryMutation();

  const lotteryItems = useMemo(() => {
    return inventoryItems
      .filter((item) => item.sku.type === "LOTTERY")
      .map((item) => item.sku);
  }, [inventoryItems]);

  const lotteryEntries = lotteryEntriesData?.hardwareLotteryEntries || [];

  const handleApprove = async (entry: LotteryEntry) => {
    try {
      console.log(
        "Attempting to approve lottery entry for user:",
        entry.user?.name
      );

      // Use the new approveLotteryEntry mutation to directly approve this specific entry
      await approveLotteryEntry({
        variables: {
          id: entry.id,
        },
      });

      successToast(
        `Successfully approved ${entry.user?.name} for the special item! A hardware request has been created and they will receive an email notification.`
      );
      await refetch();
    } catch (error) {
      console.error("Failed to approve lottery entry:", error);

      // Provide specific error messages based on the error type
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      if (errorMessage?.includes("already has this lottery item")) {
        errorToast(
          `Cannot approve ${entry.user?.name} because they already have this special item.`
        );
      } else if (errorMessage?.includes("No units available")) {
        errorToast(
          `Cannot approve ${entry.user?.name} because there are no units available for this item.`
        );
      } else if (errorMessage?.includes("not in ENTERED status")) {
        errorToast(
          `Cannot approve ${entry.user?.name} because this special item request is no longer in ENTERED status.`
        );
      } else {
        errorToast(
          `Failed to approve special item request: ${
            errorMessage || "Unknown error"
          }`
        );
      }
    }
  };

  if (
    hardwareCategoriesDataLoading ||
    hardwareSkusDataLoading ||
    hardwareItemsLoading
  ) {
    return <Spinner text={loadingMessage} />;
  }

  return (
    <>
      <Header
        title="Special Item Management"
        subtitle="Review and approve special item requests"
      />
      <PageWrapper>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            marginBottom: "48px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <Text type="h2">Special Item Requests</Text>
            <Text
              type="body1"
              style={{ color: globalConstants?.color?.textSecondary as string }}
            >
              Select a special item to view and approve requests.
            </Text>
          </div>
        </div>

        <LotteryItemsSection>
          <Text type="h3" style={{ marginBottom: "16px" }}>
            Special Items
          </Text>
          <LotteryItemsGrid>
            {lotteryItems.map((item) => (
              <LotteryItemCard
                key={item.code}
                onClick={() => setSelectedSku(item.code)}
                isSelected={selectedSku === item.code}
              >
                <img src={item.image} alt={item.name} />
                <div>
                  <Text type="body1" style={{ fontWeight: 600 }}>
                    {item.name}
                  </Text>
                </div>
              </LotteryItemCard>
            ))}
          </LotteryItemsGrid>
        </LotteryItemsSection>

        {selectedSku && (
          <>
            <Divider style={{ margin: "32px 0" }} />
            <LotteryEntriesSection>
              <Text type="h3" style={{ marginBottom: "16px" }}>
                Requests for{" "}
                {lotteryItems.find((item) => item.code === selectedSku)?.name}
              </Text>

              {lotteryEntriesLoading ? (
                <Spinner text="Loading special item requests..." />
              ) : lotteryEntries.length === 0 ? (
                <Text
                  type="body1"
                  style={{
                    display: "block",
                    color: globalConstants?.color?.textSecondary as string,
                  }}
                >
                  No special item requests found for this item.
                </Text>
              ) : (
                <Table
                  displayStyle="plain"
                  columns={[
                    { id: "user", name: "User" },
                    { id: "status", name: "Status" },
                    { id: "created_at", name: "Request Date" },
                    { id: "actions", name: "Actions" },
                  ]}
                  data={lotteryEntries.map((entry) => ({
                    user: entry.user?.name || "Unknown",
                    status: (
                      <Label
                        size="sm"
                        color={
                          entry.status === "ENTERED"
                            ? "warning-light"
                            : (entry.status as string) === "WON"
                            ? "success-light"
                            : (entry.status as string) === "RETURNED"
                            ? "primary-light"
                            : "secondary-light"
                        }
                      >
                        {entry.status}
                      </Label>
                    ),
                    created_at: new Date(entry.created_at).toLocaleString(),
                    actions:
                      entry.status === "ENTERED" &&
                      !entry.hardware_request_id ? (
                        <Button
                          size="sm"
                          color="primary"
                          onClick={() => handleApprove(entry)}
                        >
                          Approve
                        </Button>
                      ) : (entry.status as string) === "WON" ? (
                        <Label size="sm" color="success-light">
                          Won & Checked Out
                        </Label>
                      ) : (entry.status as string) === "RETURNED" ? (
                        <Label size="sm" color="primary-light">
                          Returned
                        </Label>
                      ) : (
                        <Label size="sm" color="secondary-light">
                          {entry.hardware_request_id ? "Accepted" : "Withdrawn"}
                        </Label>
                      ),
                  }))}
                />
              )}
            </LotteryEntriesSection>
          </>
        )}
      </PageWrapper>
    </>
  );
};

const LotteryItemsSection = styled.div`
  margin-bottom: 32px;
`;

const LotteryItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
`;

const LotteryItemCard = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 2px solid
    ${({ isSelected, theme }) =>
      isSelected
        ? theme.globalConstants.color.brandPrimary
        : theme.globalConstants.color.textLight};
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s;
  background: ${({ theme }) => theme.globalConstants.color.white};

  &:hover {
    border-color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  }

  img {
    width: 48px;
    height: 48px;
    object-fit: contain;
    border-radius: 4px;
    background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
  }
`;

const LotteryEntriesSection = styled.div`
  margin-top: 32px;
`;

export default OrganizerLottery;
